import React, { useState } from 'react';
import styles from '../WarrantyRequestModal.module.scss';
import clsx from 'clsx';
import { Input } from '../../../../components/uikit/inputs/Input.tsx';
import { Autocomplete, TextField } from '@mui/material';
import { SelectOption } from './SelectOption.tsx';
import { TextArea } from '../../../../components/uikit/inputs/TextArea.tsx';
import { AdvancedFormReturnType } from '../../../../helpers/form/useAdvancedForm.ts';
import {
  Form,
  Option,
  WarrantyRequestModalCategory,
} from '../WarrantyRequestModal.tsx';
import { DeviceType } from '../../../../services/api/api-client.types.ts';
import { useScopedTranslation } from '../../../../application/localization/useScopedTranslation.ts';
import CloseIcon from 'assets/icons/close-button-small.svg?react';
import ArrowDown from 'assets/icons/arrow-down-selector.svg?react';
import { RadioButton } from '../../../../components/uikit/RadioButton.tsx';
import { useWatch } from 'react-hook-form';
import { Loading } from '../../../../components/uikit/suspense/Loading.tsx';
import ResetIcon from 'assets/icons/reset.svg?react';
import { useAnalytics } from 'helpers/analytics.ts';
import { removeExtraLetters } from '../warranty-helper.ts';

type OwnProps = {
  form: AdvancedFormReturnType<Form>;
  replacementPart: string | null;
  replacementPartNumber?: string | null;
  deviceType: DeviceType | undefined;
  isLoading: boolean;
  selectedErrors: Option[];
  availableErrors: Option[];
  removeSelectedError: (error: Option) => void;
  showClearErrorsModal: () => void;
  article: string;
  errorDescription: string;
  isEditingMode?: boolean;
};

export const getCodeAndTextOfError = (text: string) => {
  const regex = /^\d+(\s|$)/;
  const match = text.match(regex);

  let optionCode = NaN;
  let optionText = text;

  if (match) {
    optionCode = parseInt(text.split(' ')[0]);
    optionText = text.replace(String(optionCode), '');
  }
  optionText = optionText.replace('^', '');
  return { optionText, optionCode };
};

const regex =
  /[^\u0021-\u007E\u00C0-\u00FF\u0100-\u017F\u0180-\u024F\u0400-\u04FF\s]*/g;

const symbolRegex = /['"\\/<>&#;%=\(\)\{\}\[\]#$]/;

export const ArticleInfo = (props: OwnProps) => {
  const {
    form,
    replacementPart,
    replacementPartNumber,
    selectedErrors,
    availableErrors,
    removeSelectedError,
    article,
    deviceType,
    isLoading,
    errorDescription,
    showClearErrorsModal,
    isEditingMode,
  } = props;
  const [inputValue, setInputValue] = useState('');
  const i18n = useScopedTranslation('Page.WarrantyRequest');
  const { trackEvent } = useAnalytics();
  const isReplacementPartNotFilled = replacementPartNumber !== '';
  const [duringTherapy, notDuringTherapy] = useWatch({
    control: form.control,
    name: ['duringTherapy', 'notDuringTherapy'],
  });

  const chooseDuringTherapy = () => {
    form.clearErrors('isDurationSelected');
    form.setValue('duringTherapy', true, { shouldDirty: true });
    form.setValue('notDuringTherapy', false, { shouldDirty: true });
  };

  const chooseNotDuringTherapy = () => {
    form.clearErrors('isDurationSelected');
    form.setValue('notDuringTherapy', true, { shouldDirty: true });
    form.setValue('duringTherapy', false, { shouldDirty: true });
  };

  const emptyErrorListUsualDevice =
    !availableErrors.length && deviceType !== DeviceType.Special;
  const emptyErrorListSpecialDevice =
    !availableErrors.length &&
    deviceType === DeviceType.Special &&
    (duringTherapy || notDuringTherapy);

  return (
    <div className={styles.comboBoxWrapper}>
      <div
        className={clsx(
          styles.rowContainer,
          styles.columnGap135,
          !replacementPartNumber && isReplacementPartNotFilled
            ? styles.marginBottom70
            : styles.marginBottom40,
        )}
      >
        <div className={clsx(styles.columnContainer, styles.rowGap13)}>
          <div className={clsx(styles.label)}>{i18n.t('article')}</div>
          <div data-test-id={'article'} className={styles.text}>
            {removeExtraLetters(article)}
          </div>
        </div>
        <div className={clsx(styles.columnContainer, styles.rowGap13)}>
          <div className={clsx(styles.label)}>{i18n.t('description')}</div>
          <div
            data-test-id={'description-spare-part'}
            className={clsx(styles.text)}
          >
            {replacementPart}
          </div>
        </div>
      </div>
      {!replacementPartNumber && isReplacementPartNotFilled ? (
        <Input
          testId={'part-sn-input'}
          {...form.register('articleSerialNumber')}
          errorText={form.formState.errors.articleSerialNumber?.message}
          topHelperText={i18n.t('serial_number')}
          className={clsx(styles.width512, styles.marginBottom52)}
        />
      ) : (
        <>
          <div className={clsx(styles.label, styles.marginBottom10)}>
            {i18n.t('serial_number')}
          </div>
          {isReplacementPartNotFilled ? (
            <div
              data-test-id={'serial-number'}
              className={clsx(styles.text, styles.marginBottom52)}
            >
              {replacementPartNumber}
            </div>
          ) : (
            <div className={clsx(styles.label, styles.marginBottom52)}>
              {i18n.t('not_filled')}
            </div>
          )}
        </>
      )}
      <div
        className={clsx(
          styles.marginBottom10,
          styles.rowContainer,
          styles.columnGap4,
        )}
      >
        <div className={clsx(styles.subtitle)}>{i18n.t('error')}</div>
        {!!(
          errorDescription ||
          (availableErrors.length && deviceType === DeviceType.Special) ||
          selectedErrors.length
        ) &&
          !isEditingMode && (
            <div
              className={clsx(
                styles.rowContainer,
                styles.columnGap4,
                styles.resetButton,
              )}
              data-test-id={'reset-errors-button'}
              onClick={showClearErrorsModal}
            >
              <ResetIcon />
              <div className={clsx(styles.label, styles.fontWeight500)}>
                {i18n.t('reset')}
              </div>
            </div>
          )}
      </div>
      {(deviceType === DeviceType.Special ||
        duringTherapy ||
        notDuringTherapy) && (
        <div className={clsx(styles.marginBottom40)}>
          <div
            className={clsx(
              styles.rowContainer,
              styles.justifyContentSpaceBetween,
            )}
          >
            <div className={clsx(styles.label, styles.marginBottom10)}>
              {i18n.t('device_failed')}
            </div>
            <div className={clsx(styles.label, styles.disabled)}>
              {i18n.t('required')}
            </div>
          </div>
          <RadioButton
            testId={'not-during-therapy-radiobutton'}
            name={'failure-timing'}
            title={i18n.t('not_during_therapy')}
            value={'not_during_therapy'}
            checked={notDuringTherapy}
            onChange={chooseNotDuringTherapy}
            className={styles.marginBottom10}
          />
          <RadioButton
            name={'failure-timing'}
            title={i18n.t('during_therapy')}
            value={'during_therapy'}
            checked={duringTherapy}
            onChange={chooseDuringTherapy}
          />
          {form.formState.errors.isDurationSelected && (
            <div
              className={clsx(
                styles.text,
                styles.errorText,
                styles.marginTop10,
              )}
            >
              {i18n.t('required_error')}
            </div>
          )}
        </div>
      )}
      {!!availableErrors.length &&
        !(
          deviceType === DeviceType.Special &&
          !duringTherapy &&
          !notDuringTherapy
        ) && (
          <div className={styles.relativeContainer}>
            <div
              className={clsx(
                styles.rowContainer,
                styles.justifyContentSpaceBetween,
                styles.marginBottom4,
              )}
            >
              <div className={clsx(styles.label)}>{i18n.t('error_name')}</div>
              <div className={clsx(styles.label, styles.disabled)}>
                {i18n.t('required')}
              </div>
            </div>
            <Autocomplete
              multiple
              data-test-id={'error-select'}
              className={clsx(
                styles.errorSelectorBorder,
                form.formState.errors.areAnyErrorsSelected &&
                  styles.errorSelectorRedBorder,
              )}
              size="small"
              value={selectedErrors}
              options={availableErrors}
              isOptionEqualToValue={(a, b) => a.key === b.key}
              onChange={(e, newValue) => {
                trackEvent({
                  category: WarrantyRequestModalCategory,
                  action: 'Select error',
                  name: `Error text: ${newValue.at(-1)?.text}; error key: ${
                    newValue.at(-1)?.key
                  }`,
                });

                form.clearErrors('areAnyErrorsSelected');
                form.setValue('selectedErrors', newValue, {
                  shouldDirty: true,
                });
              }}
              onInputChange={(e, value) => setInputValue(value)}
              getOptionLabel={(option) => option.text}
              renderInput={(params) => (
                <TextField
                  {...params}
                  className={clsx(styles.placeholder)}
                  placeholder={i18n.t('input_error_placeholder')}
                />
              )}
              disableCloseOnSelect={false}
              popupIcon={<ArrowDown onMouseDown={(e) => e.preventDefault()} />}
              disablePortal={true}
              renderOption={(props, option) => (
                <SelectOption
                  testId={'error-item'}
                  key={option.key}
                  option={option}
                  liProps={props}
                  selectedOptions={selectedErrors}
                  inputValue={inputValue}
                />
              )}
            />
            {form.formState.errors.areAnyErrorsSelected && (
              <div
                className={clsx(
                  styles.text,
                  styles.errorText,
                  styles.absoluteContainer,
                )}
              >
                {i18n.t('required_error')}
              </div>
            )}
            <div
              className={clsx(
                styles.rowContainer,
                styles.columnGap5,
                styles.selectedErrorContainer,
              )}
            >
              {selectedErrors.map((error) => {
                const { optionText, optionCode } = getCodeAndTextOfError(
                  error.text,
                );
                return (
                  <div
                    key={error.key}
                    className={clsx(
                      styles.rowContainer,
                      styles.columnGap5,
                      styles.selectedError,
                    )}
                  >
                    <div
                      data-test-id={'error-chip'}
                      className={clsx(styles.rowContainer, styles.columnGap5)}
                    >
                      {!isNaN(optionCode) && (
                        <div className={styles.chipCode}>{optionCode}</div>
                      )}
                      <div>
                        {optionText.length
                          ? optionText.trim()
                          : i18n.t('option_error')}
                      </div>
                    </div>
                    <CloseIcon
                      className={styles.icon}
                      onClick={() => {
                        trackEvent({
                          category: WarrantyRequestModalCategory,
                          action: 'Remove selected error',
                          name: `Error text: ${error.text}; error key: ${error.key}`,
                        });
                        removeSelectedError(error);
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      {!isLoading &&
        (emptyErrorListUsualDevice || emptyErrorListSpecialDevice) && (
          <div className={clsx(styles.text, styles.errorText)}>
            {i18n.t('empty_error_list')}
          </div>
        )}
      {!(
        deviceType === DeviceType.Special &&
        !duringTherapy &&
        !notDuringTherapy
      ) && (
        <TextArea
          testId={'comment-input'}
          {...form.register('errorDescription')}
          onChange={(e) => {
            form.setValue(
              'errorDescription',
              e.target.value.replace(regex, '').replace(symbolRegex, ''),
              {
                shouldDirty: true,
              },
            );
          }}
          className={clsx(styles.height120)}
          containerClassName={styles.marginTop30}
          fullWidth={true}
          topHelperText={i18n.t('comment')}
          placeholder={i18n.t('comment_description')}
        />
      )}
      <Loading loading={isLoading} wrapperClassName={styles.loader} />
    </div>
  );
};
